/**
 * Deterministic, local, universal culinary recipe recognizer for RECIPE V2.
 * No provider call, no network. Provider calls = 0. AI cost = $0.
 *
 * Recognition is structural, not vocabulary-gated. Culinary words may help
 * only through generic action detection; component headings are not limited
 * to a confectionery allow-list.
 */

function compact(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function splitParagraphs(text) {
  return String(text || "")
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n+/)
    .map((p) => p.replace(/^[ \t]+|[ \t]+$/gm, "").replace(/^\n+|\n+$/g, ""))
    .filter((p) => p.trim());
}

function stripListPrefix(value) {
  let text = String(value || "").trim();
  if (!text) return "";

  // Remove presentation-only list junk: bullets, geometric markers, emoji
  // markers and simple enumerators. Repeat because real copied recipes often
  // contain combinations such as "✅ • 1. ингредиент".
  let previous = null;
  while (text && text !== previous) {
    previous = text;
    text = text
      .replace(/^\s*(?:[-–—•*·▪▫●○◦‣⁃■□◆◇►▸▶✓✔☑]+)\s*/u, "")
      .replace(/^\s*(?:\p{Extended_Pictographic}\uFE0F?)+\s*/u, "")
      .replace(/^\s*(?:\d{1,3}|[A-Za-zА-Яа-яЁё])[.)\-:]\s+/u, "")
      .trim();
  }
  return text;
}

const EMOJI_RE = /\p{Extended_Pictographic}/u;
const SOCIAL_RE = /^(?:@\S+|#\S+)(?:\s+(?:@\S+|#\S+))*$/u;
const PROMO_RE = /(?:подписывайтесь|подпишитесь|сохраняйте\s+рецепт|сохрани\s+рецепт|сохраните\s+рецепт|ставьте\s+лайк|поставьте\s+лайк|делитесь|отмечайте)/iu;

function isObviousNoiseLine(line) {
  const t = compact(line);
  if (!t) return true;
  if (SOCIAL_RE.test(t) || PROMO_RE.test(t)) return true;
  return !/\p{L}/u.test(t) && EMOJI_RE.test(t);
}

function isAmbiguousLifestyleLine(line) {
  const t = compact(line);
  return Boolean(t && EMOJI_RE.test(t) && /\p{L}/u.test(t) && !PROMO_RE.test(t));
}

const ACTION_RE = /(?:^|\s)(?:взб\w*|просе\w*|перемеш\w*|смеш\w*|соедин\w*|добав\w*|вли\w*|выл\w*|всып\w*|ввести|ввод\w*|выпек(?:ать|айте|аем|ают|ается|ивать|ивать)?|печь|готов(?:ить|ьте|им|ят|ится)|приготов\w*|нагре\w*|подогре\w*|довест\w*|довод\w*|вар(?:ить|ите|им|ят|ится)|обжар\w*|жар(?:ить|ьте|им|ят|ится)|туш\w*|охлад\w*|остуд\w*|замороз\w*|остав\w*|дать|накры\w*|раздел\w*|раскат\w*|сформ\w*|формовать|формируем|формируйте|сложить|складывать|складываем|складывайте|отсад\w*|нанес\w*|распредел\w*|собер\w*|собир\w*|украс\w*|нарез\w*|измельч\w*|растоп\w*|процед\w*|ферментировать|марин\w*|обвал\w*|запек\w*|постав\w*|убер\w*)/iu;

const TECH_LEAD_RE = /^(?:до|при|после|перед|затем|далее|потом|в\s+течение|примерно|около)\b/iu;
const TIME_TEMP_ONLY_RE = /^\d+(?:[.,]\d+)?(?:\s*[–-]\s*\d+(?:[.,]\d+)?)?\s*(?:°\s?c|мин(?:ут[аы]?|\.)?|час(?:а|ов)?|ч\.?|сек(?:унд[аы]?|\.)?|градус(?:а|ов)?)\b/iu;

const QTY = "(?:\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:[.,]\\d+)?(?:\\s*[–-]\\s*\\d+(?:[.,]\\d+)?)?|[½¼¾])";
const UNIT = "(?:г|гр\\.?|грамм(?:а|ов)?|кг|мл|л|шт\\.?|штук(?:а|и)?|зубчик(?:а|ов)?|пуч(?:ок|ка|ков)|веточк(?:а|и|ек)|банк(?:а|и|ок)|упаковк(?:а|и|ок)|ломтик(?:а|ов)?|лист(?:а|ьев)?|головк(?:а|и|ок)|ст\\.\\s?л\\.?|столов(?:ая|ых?)\\s+ложк(?:а|и|ек)|ч\\.\\s?л\\.?|чайн(?:ая|ых?)\\s+ложк(?:а|и|ек)|щепотк[аиу])";
const QTY_FIRST_RE = new RegExp(`^(${QTY})\\s*(?:(${UNIT})\\s+)?(.+)$`, "iu");
const NAME_FIRST_RE = new RegExp(`^(.+?)\\s*[—–-]\\s*(${QTY})\\s*(${UNIT})?\\s*$`, "iu");

function normalizeQty(value) {
  return compact(value).replace(",", ".");
}

function normalizeUnit(value) {
  return compact(value || "");
}

function ingredientMatch(rawLine) {
  const line = stripListPrefix(rawLine).replace(/[.;]$/, "").trim();
  if (!line || TIME_TEMP_ONLY_RE.test(line) || ACTION_RE.test(line)) return null;

  const wordQty = /^(щепотк[аиу])\s+(.+)$/iu.exec(line);
  if (wordQty) {
    return { name: compact(wordQty[2]), qty: "1", unit: compact(wordQty[1]), note: "" };
  }

  const toTaste = /^(.+?)\s*[—–-]\s*по\s+вкусу$/iu.exec(line);
  if (toTaste) {
    const name = compact(toTaste[1]);
    if (!name) return null;
    return { name, qty: "по вкусу", unit: "", note: "" };
  }

  let m = NAME_FIRST_RE.exec(line);
  if (m) {
    const name = compact(m[1]);
    if (!name || /^\d/.test(name)) return null;
    return { name, qty: normalizeQty(m[2]), unit: normalizeUnit(m[3]), note: "" };
  }

  m = QTY_FIRST_RE.exec(line);
  if (!m) return null;
  const qty = normalizeQty(m[1]);
  const unit = normalizeUnit(m[2]);
  const name = compact(m[3]);
  if (!name || /^°/u.test(name)) return null;

  // Bare quantity without a unit is accepted only for count-like ingredient
  // phrases (e.g. "1 яйцо"), never for ordinary prose.
  if (!unit) {
    if (!/^\p{L}[\p{L}\p{M}-]*(?:\s+[СC]\d|\s+[SMLX]{1,2})?$/u.test(name)) return null;
  }
  return { name, qty, unit, note: "" };
}

function looksLikeIngredientLine(line) {
  return Boolean(ingredientMatch(line));
}

let idSeq = 0;
function newId(prefix) {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
}

function parseIngredientLine(line) {
  const m = ingredientMatch(line);
  if (!m) return null;
  return { id: newId("ing"), ...m };
}

function matchYield(raw) {
  const t = compact(raw);
  let m = /^выход\s*[:—–-]\s*(.+)$/iu.exec(t);
  if (m) return compact(m[1]);
  m = /^на\s+(\d+(?:\s*[–-]\s*\d+)?\s*(?:порци[йя]|шт\.?))\.?$/iu.exec(t);
  if (m) return compact(m[1]);
  m = /^(\d+(?:\s*[–-]\s*\d+)?\s+порци[йя])\.?$/iu.exec(t);
  if (m) return compact(m[1]);
  return null;
}

function isHeadingShape(rawLine) {
  const raw = stripListPrefix(rawLine);
  const stripped = raw.replace(/:\s*$/, "").trim();
  if (!stripped || stripped.length > 70) return false;
  if (!/^\p{L}/u.test(stripped)) return false;
  if (/[.!?…]/u.test(stripped)) return false;
  if (looksLikeIngredientLine(stripped) || matchYield(stripped)) return false;
  if (ACTION_RE.test(stripped) || TECH_LEAD_RE.test(stripped) || TIME_TEMP_ONLY_RE.test(stripped)) return false;
  return true;
}

function paragraphLines(paragraph) {
  return String(paragraph || "").split("\n").map((l) => l.trim()).filter(Boolean);
}

function paragraphIsIngredientBlock(paragraph) {
  const lines = paragraphLines(paragraph).filter((l) => !isObviousNoiseLine(l));
  return lines.length > 0 && lines.every(looksLikeIngredientLine);
}

function paragraphStartsHeading(paragraph) {
  const lines = paragraphLines(paragraph);
  if (!lines.length) return false;
  if (!isHeadingShape(lines[0])) return false;
  if (/[:：]\s*$/.test(lines[0])) return true;
  if (lines.length > 1) return true;
  return false;
}

function nextMeaningfulParagraph(paragraphs, from) {
  for (let i = from; i < paragraphs.length; i += 1) {
    const lines = paragraphLines(paragraphs[i]).filter((l) => !isObviousNoiseLine(l));
    if (lines.length) return { index: i, paragraph: paragraphs[i], lines };
  }
  return null;
}

function hasLaterSectionHeading(paragraphs, from) {
  for (let i = from; i < paragraphs.length; i += 1) {
    const lines = paragraphLines(paragraphs[i]).filter((l) => !isObviousNoiseLine(l));
    if (!lines.length) continue;
    if (isHeadingShape(lines[0]) && (/[:：]\s*$/.test(lines[0]) || lines.length > 1 || i + 1 < paragraphs.length)) return true;
  }
  return false;
}

function titleCandidate(paragraphs, index) {
  const lines = paragraphLines(paragraphs[index]).filter((l) => !isObviousNoiseLine(l));
  if (lines.length !== 1) return false;
  const line = compact(lines[0]);
  if (!isHeadingShape(line)) return false;

  const next = nextMeaningfulParagraph(paragraphs, index + 1);
  if (!next) return false;
  if (matchYield(compact(next.paragraph))) return true;

  // A first standalone noun phrase followed by another clear section heading
  // is a title regardless of word count: "Яблочная галета" -> "Тесто".
  if (isHeadingShape(next.lines[0]) && (/[:：]\s*$/.test(next.lines[0]) || next.lines.length > 1)) return true;

  // If the next paragraph is an ingredient list and later comparable headings
  // exist, the first line is much more likely the first component ("Опара")
  // than the recipe title. Without later headings, it is safely the title of a
  // simple single-component recipe ("Шницель", "Простой омлет").
  if (paragraphIsIngredientBlock(next.paragraph)) {
    return !hasLaterSectionHeading(paragraphs, next.index + 1);
  }

  // A short descriptive sentence after the first line is good title evidence.
  const nextText = compact(next.paragraph);
  if (/[.!?…]$/u.test(nextText) && !ACTION_RE.test(nextText)) return true;

  return false;
}

function isSubtitleParagraph(paragraph) {
  const lines = paragraphLines(paragraph).filter((l) => !isObviousNoiseLine(l));
  if (lines.length !== 1) return false;
  const text = compact(lines[0]);
  if (!text || text.length > 400) return false;
  if (matchYield(text) || looksLikeIngredientLine(text) || isHeadingShape(text)) return false;
  if (ACTION_RE.test(text) || isAmbiguousLifestyleLine(text)) return false;
  return true;
}

function appendTechnology(component, text) {
  const chunk = String(text || "").trim();
  if (!chunk) return;
  component.technology = component.technology ? `${component.technology}\n\n${chunk}` : chunk;
}

function looksLikeDescriptivePreamble(line) {
  const t = compact(line);
  if (!t || looksLikeIngredientLine(t) || matchYield(t) || isHeadingShape(t)) return false;
  if (ACTION_RE.test(t) || TECH_LEAD_RE.test(t) || TIME_TEMP_ONLY_RE.test(t)) return false;
  return t.length <= 420;
}

function looksLikeUnquantifiedIngredient(line) {
  const t = stripListPrefix(line).replace(/[.;:]$/, "").trim();
  if (!t || t.length > 70) return false;
  if (/[.!?…]/u.test(t)) return false;
  if (ACTION_RE.test(t) || TECH_LEAD_RE.test(t) || TIME_TEMP_ONLY_RE.test(t)) return false;
  if (matchYield(t) || isHeadingShape(t)) return false;
  const words = t.split(/\s+/).filter(Boolean);
  return words.length >= 1 && words.length <= 6 && words.every((word) => /[\p{L}\p{M}%+-]/u.test(word));
}

/**
 * @returns {{source_text:string,title:string,subtitle:string,yield:string,components:Array,unresolved_lines:Array}}
 */
export function structureRecipeSource(rawText) {
  idSeq = 0;
  const sourceText = String(rawText || "");
  const rawLines = sourceText.replace(/\r\n?/g, "\n").split("\n");
  const components = [];
  const unresolvedLines = [];
  let title = "";
  let subtitle = "";
  let yieldText = "";
  let current = null;
  let seenComponent = false;

  const ensureCurrent = () => {
    if (!current) {
      current = { id: newId("comp"), title: "", ingredients: [], technology: "" };
      components.push(current);
    }
    return current;
  };

  const nextMeaningfulLine = (from) => {
    for (let i = from; i < rawLines.length; i += 1) {
      const line = rawLines[i].trim();
      if (!line || isObviousNoiseLine(line)) continue;
      return { index: i, line };
    }
    return null;
  };

  const isStrongHeadingAt = (index) => {
    const line = rawLines[index]?.trim() || "";
    if (!isHeadingShape(line)) return false;
    if (/[:：]\s*$/.test(line)) return true;
    const next = nextMeaningfulLine(index + 1);
    if (!next) return false;
    if (looksLikeIngredientLine(next.line)) return true;
    if (ACTION_RE.test(next.line) || TECH_LEAD_RE.test(next.line)) return true;
    // A short heading followed by another short heading is also structural.
    if (isHeadingShape(next.line) && next.line.length <= 60) return true;
    return false;
  };

  // Conservative title detection: first meaningful standalone line only when
  // the following structure looks like recipe content. Otherwise keep title
  // empty and let the Owner name it manually.
  const first = nextMeaningfulLine(0);
  if (first && isHeadingShape(first.line) && !/[:：]\s*$/.test(first.line)) {
    const next = nextMeaningfulLine(first.index + 1);
    if (next && !looksLikeIngredientLine(next.line) && !ACTION_RE.test(next.line) && !isStrongHeadingAt(first.index)) {
      const after = nextMeaningfulLine(next.index + 1);
      if (after && (isStrongHeadingAt(after.index) || looksLikeIngredientLine(after.line))) {
        title = compact(first.line);
        rawLines[first.index] = "";
      }
    }
  }

  for (let i = 0; i < rawLines.length; i += 1) {
    const original = rawLines[i];
    const line = original.trim();
    if (!line || isObviousNoiseLine(line)) continue;

    if (!yieldText && !seenComponent) {
      const y = matchYield(line);
      if (y) { yieldText = y; continue; }
    }

    if (isAmbiguousLifestyleLine(line)) {
      unresolvedLines.push(line);
      continue;
    }

    if (isStrongHeadingAt(i)) {
      current = {
        id: newId("comp"),
        title: compact(line).replace(/[:：]\s*$/, ""),
        ingredients: [],
        technology: "",
      };
      components.push(current);
      seenComponent = true;
      continue;
    }

    if (!seenComponent) {
      // Descriptive copy before the first real component belongs to subtitle,
      // never to ingredient/technology content.
      if (looksLikeDescriptivePreamble(line)) {
        subtitle = subtitle ? `${subtitle} ${compact(line)}` : compact(line);
        continue;
      }
      // Ingredient lines before a detected heading are kept in an implicit
      // component only when there truly is no component heading yet.
      const preIng = parseIngredientLine(line);
      if (preIng) {
        ensureCurrent().ingredients.push(preIng);
        continue;
      }
    }

    const comp = ensureCurrent();
    const ing = parseIngredientLine(line);
    if (ing) {
      comp.ingredients.push(ing);
      continue;
    }

    // Before technology prose starts, short non-action culinary lines are
    // preserved as unquantified ingredients (e.g. "вся опара", "молоко",
    // "тёртый пармезан", "салатные листья"). Never invent a quantity/unit.
    if (!compact(comp.technology) && looksLikeUnquantifiedIngredient(line)) {
      comp.ingredients.push({
        id: newId("ing"),
        name: stripListPrefix(line).replace(/[.;:]$/, "").trim(),
        qty: "",
        unit: "",
        note: "",
      });
      continue;
    }

    appendTechnology(comp, stripListPrefix(line));
  }

  const cleanComponents = components.filter((comp) =>
    comp.title || comp.ingredients.length || compact(comp.technology));

  return {
    source_text: sourceText,
    title,
    subtitle,
    yield: yieldText,
    components: cleanComponents,
    unresolved_lines: unresolvedLines,
  };
}